import { useEffect, useState, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, TrendingUp, TrendingDown, Eye, Ear, Hand, BookOpen, Sparkles, CalendarClock, Clock, FileArchive, Cross, FileUp } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { relativeTime } from "@/lib/time";
import { Sparkline } from "@/components/Sparkline";
import AssessmentsPanel from "@/components/AssessmentsPanel";
import AttendanceCard from "@/components/AttendanceCard";
import VerseTracker from "@/components/VerseTracker";
import PortfolioActions from "@/components/PortfolioActions";
import PortfolioDocumentsPanel from "@/components/PortfolioDocumentsPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const STYLE_META = {
  visual: { label: "Visual", icon: Eye, bg: "bg-red-50", text: "text-red-700" },
  auditory: { label: "Auditory", icon: Ear, bg: "bg-blue-50", text: "text-blue-700" },
  kinesthetic: { label: "Kinesthetic", icon: Hand, bg: "bg-violet-50", text: "text-violet-700" },
  reading: { label: "Reading/Writing", icon: BookOpen, bg: "bg-amber-50", text: "text-amber-700" },
};

function scoreBand(score) {
  if (score >= 75) return { label: "Strength", color: "text-emerald-600", bar: "bg-emerald-500", ring: "ring-emerald-200" };
  if (score >= 55) return { label: "On track", color: "text-amber-600", bar: "bg-amber-500", ring: "ring-amber-200" };
  return { label: "Needs focus", color: "text-red-600", bar: "bg-red-500", ring: "ring-red-200" };
}

export default function StudentProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [scores, setScores] = useState({});   // subject -> {score, notes}
  const [history, setHistory] = useState({}); // subject -> [{score, at}, ...]
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/students/${id}/profile`);
      setProfile(data);
      const initial = {};
      for (const s of data.subjects_catalog) {
        const entry = (data.proficiencies || {})[s] || { score: 60, notes: "" };
        initial[s] = { score: entry.score ?? 60, notes: entry.notes ?? "", learning_style: entry.learning_style || "", last_assessed: entry.last_assessed };
      }
      setScores(initial);
      // Fetch history
      const h = await api.get(`/students/${id}/history`);
      setHistory(h.data.history || {});
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      nav("/dashboard/students");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { proficiencies: {} };
      for (const [subject, entry] of Object.entries(scores)) {
        payload.proficiencies[subject] = {
          score: entry.score,
          notes: entry.notes,
          learning_style: entry.learning_style || null,
        };
      }
      const { data } = await api.patch(`/students/${id}/proficiencies`, payload);
      setProfile((p) => ({ ...p, ...data }));
      // Merge last_assessed back into local scores
      const merged = { ...scores };
      for (const [subject, entry] of Object.entries(data.proficiencies || {})) {
        merged[subject] = { ...(merged[subject] || {}), last_assessed: entry.last_assessed };
      }
      setScores(merged);
      // Refresh history so sparklines pick up the new snapshot
      const h = await api.get(`/students/${id}/history`);
      setHistory(h.data.history || {});
      toast.success("Profile saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleChristian = async (enabled) => {
    // Optimistic update
    setProfile((p) => ({ ...p, christian_curriculum: enabled }));
    try {
      await api.patch(`/students/${id}`, { christian_curriculum: enabled });
      toast.success(enabled ? "Christian curriculum enabled — lesson plans will weave in Bible focus" : "Christian curriculum disabled");
    } catch (e) {
      setProfile((p) => ({ ...p, christian_curriculum: !enabled }));
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const saveDefaultStyle = async (newStyle) => {
    const prev = profile.learning_style;
    setProfile((p) => ({ ...p, learning_style: newStyle }));
    try {
      await api.patch(`/students/${id}`, { learning_style: newStyle });
      toast.success(`Default style set to ${newStyle}`);
    } catch (e) {
      setProfile((p) => ({ ...p, learning_style: prev }));
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const [suggestBusy, setSuggestBusy] = useState(false);
  const [suggestions, setSuggestions] = useState(null);

  const suggestStyles = async () => {
    setSuggestBusy(true);
    try {
      const { data } = await api.get(`/students/${id}/style-suggestions`);
      setSuggestions(data);
      if (!data.suggestions || Object.keys(data.suggestions).length === 0) {
        toast.info("No strong overrides suggested — current styles look good.");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSuggestBusy(false);
    }
  };

  const applySuggestions = () => {
    if (!suggestions?.suggestions) return;
    setScores((prev) => {
      const next = { ...prev };
      for (const [subj, s] of Object.entries(suggestions.suggestions)) {
        next[subj] = { ...(next[subj] || { score: 60, notes: "" }), learning_style: s.style };
      }
      return next;
    });
    setSuggestions(null);
    toast.success("Suggestions applied — click Save profile to persist");
  };

  const { strengths, weaknesses } = useMemo(() => {
    const strengths = [];
    const weaknesses = [];
    for (const [subj, entry] of Object.entries(scores)) {
      if (entry.score >= 75) strengths.push(subj);
      else if (entry.score < 55) weaknesses.push(subj);
    }
    return { strengths, weaknesses };
  }, [scores]);

  if (!profile) return <div className="text-stone-500" data-testid="profile-loading">Loading profile…</div>;

  const style = STYLE_META[profile.learning_style] || STYLE_META.visual;
  const StyleIcon = style.icon;

  return (
    <div className="space-y-8" data-testid="student-profile-page">
      <Link to="/dashboard/students" className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-800" data-testid="back-to-students">
        <ArrowLeft className="w-4 h-4" /> Back to students
      </Link>

      {/* Header */}
      <div className="bg-white border border-stone-200 rounded-3xl p-8 shadow-sm">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-600">Student profile</div>
            <h1 className="mt-2 font-display text-3xl sm:text-4xl font-semibold tracking-tight" data-testid="profile-student-name">
              {profile.name}
            </h1>
            <div className="mt-2 text-stone-500">Grade {profile.grade}{profile.interests ? ` · ${profile.interests}` : ""}</div>
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <span className={`inline-flex items-center gap-1.5 rounded-full ${style.bg} ${style.text} px-3 py-1 text-xs font-semibold`}>
                <StyleIcon className="w-3 h-3" /> {style.label} learner
              </span>
              <Badge className="rounded-full bg-stone-100 text-stone-700">{profile.subscription_tier?.toUpperCase() || "BASIC"}</Badge>
              <Badge className={`rounded-full ${profile.subscription_status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-700"}`}>
                {profile.subscription_status || "trial"}
              </Badge>
              {profile.christian_curriculum && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 text-indigo-700 px-3 py-1 text-xs font-semibold" data-testid="christian-badge">
                  <Cross className="w-3 h-3" /> Christian curriculum
                </span>
              )}
            </div>

            {/* Christian curriculum toggle */}
            <div className="mt-5 flex items-center gap-3 rounded-2xl border border-stone-200 bg-stone-50/60 px-4 py-3 max-w-xl" data-testid="christian-toggle-row">
              <div className="grid place-items-center w-9 h-9 rounded-xl bg-indigo-50 text-indigo-700">
                <Cross className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <Label htmlFor="christian-toggle" className="text-sm font-semibold text-stone-800 cursor-pointer">
                  Christian-based curriculum
                </Label>
                <div className="text-xs text-stone-500 mt-0.5">
                  Blend Bible-integrated lessons + biblical worldview into planned schedules and lesson help.
                </div>
              </div>
              <Switch
                id="christian-toggle"
                checked={!!profile.christian_curriculum}
                onCheckedChange={toggleChristian}
                data-testid="christian-toggle"
              />
            </div>

            {/* Date of birth — required for the Florida Letter of Intent PDF */}
            <div className="mt-3 flex items-center gap-3 rounded-2xl border border-stone-200 bg-stone-50/60 px-4 py-3 max-w-xl" data-testid="dob-row">
              <div className="grid place-items-center w-9 h-9 rounded-xl bg-blue-50 text-blue-700">
                <FileArchive className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <Label htmlFor="dob-input" className="text-sm font-semibold text-stone-800">Date of birth</Label>
                <div className="text-xs text-stone-500 mt-0.5">Required by Florida statute on the Letter of Intent.</div>
              </div>
              <Input
                id="dob-input"
                type="date"
                value={profile.date_of_birth || ""}
                onChange={async (e) => {
                  const dob = e.target.value;
                  setProfile({ ...profile, date_of_birth: dob });
                  try {
                    await api.patch(`/students/${id}`, { date_of_birth: dob });
                  } catch (err) {
                    toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
                  }
                }}
                className="rounded-xl h-10 w-40"
                data-testid="dob-input"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to={`/dashboard/schedule?student=${id}&open=auto`} data-testid="profile-auto-build">
              <Button className="rounded-full bg-amber-500 hover:bg-amber-600 text-white">
                <CalendarClock className="w-4 h-4 mr-2" /> Auto-build a week
              </Button>
            </Link>
            <Link to={`/dashboard/ai-picks?student=${id}`} data-testid="profile-ai-picks">
              <Button variant="outline" className="rounded-full border-2 border-stone-200">
                <Sparkles className="w-4 h-4 mr-2" /> Resource picks
              </Button>
            </Link>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/portfolio/${id}/evaluation-packet`} target="_blank" rel="noreferrer" data-testid="profile-eval-packet">
              <Button variant="outline" className="rounded-full border-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                <FileArchive className="w-4 h-4 mr-2" /> Evaluation packet
              </Button>
            </a>
            <Button
              variant="outline"
              className="rounded-full border-2 border-blue-200 text-blue-700 hover:bg-blue-50"
              onClick={() => {
                const el = document.querySelector('[data-testid="portfolio-docs-panel"]');
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                setTimeout(() => document.querySelector('[data-testid="portfolio-docs-upload-btn"]')?.click(), 400);
              }}
              data-testid="profile-upload-doc-btn"
            >
              <FileUp className="w-4 h-4 mr-2" /> Upload document
            </Button>
            {profile && <PortfolioActions student={{ ...profile, id }} />}
          </div>
        </div>

        {/* Strengths / Weaknesses summary */}
        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-5" data-testid="strengths-panel">
            <div className="flex items-center gap-2 font-medium text-emerald-800">
              <TrendingUp className="w-4 h-4" /> Strengths (≥ 75)
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {strengths.length === 0 && <span className="text-sm text-emerald-800/70">None yet — bump some sliders above 75.</span>}
              {strengths.map((s) => (
                <span key={s} className="rounded-full bg-white border border-emerald-200 text-emerald-800 px-3 py-1 text-xs font-medium">{s}</span>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-red-200 bg-red-50/70 p-5" data-testid="weaknesses-panel">
            <div className="flex items-center gap-2 font-medium text-red-800">
              <TrendingDown className="w-4 h-4" /> Needs focus (&lt; 55)
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {weaknesses.length === 0 && <span className="text-sm text-red-800/70">Nothing flagged. Great!</span>}
              {weaknesses.map((s) => (
                <span key={s} className="rounded-full bg-white border border-red-200 text-red-800 px-3 py-1 text-xs font-medium">{s}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Proficiency sliders */}
      <div className="bg-white border border-stone-200 rounded-3xl p-8 shadow-sm" data-testid="proficiency-editor">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-display text-2xl font-semibold">Proficiencies</h2>
            <p className="text-sm text-stone-500 mt-1">Rate each subject 0–100 based on recent work. These scores drive personalized recommendations and auto-scheduling.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-2 rounded-full border border-stone-200 bg-white px-3 py-1.5" data-testid="default-style-row">
              <span className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold">Default style</span>
              <select
                value={profile.learning_style || "reading"}
                onChange={(e) => saveDefaultStyle(e.target.value)}
                className="bg-transparent text-sm font-semibold text-stone-800 focus:outline-none cursor-pointer"
                data-testid="default-style-select"
              >
                <option value="visual">Visual</option>
                <option value="auditory">Auditory</option>
                <option value="kinesthetic">Kinesthetic</option>
                <option value="reading">Reading/Writing</option>
              </select>
            </div>
            <Button
              onClick={suggestStyles}
              disabled={suggestBusy}
              variant="outline"
              className="rounded-full border-indigo-300 text-indigo-700 hover:bg-indigo-50"
              data-testid="suggest-styles-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" /> {suggestBusy ? "Analyzing…" : "Suggest per-subject styles"}
            </Button>
            <Button onClick={save} disabled={saving} className="rounded-full bg-stone-900 hover:bg-stone-800 text-white" data-testid="save-proficiencies">
              <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save profile"}
            </Button>
          </div>
        </div>

        {suggestions && (
          <div className="mt-6 rounded-2xl border border-indigo-200 bg-indigo-50/40 p-5" data-testid="style-suggestions-panel">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-start gap-2 min-w-0">
                <Sparkles className="w-4 h-4 text-indigo-600 shrink-0 mt-0.5" />
                <div>
                  <div className="text-xs uppercase tracking-[0.15em] font-semibold text-indigo-800">Style recommendations</div>
                  {suggestions.summary && <div className="mt-1 text-sm text-stone-700">{suggestions.summary}</div>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={applySuggestions} className="rounded-full bg-indigo-500 hover:bg-indigo-600 text-white" data-testid="apply-suggestions-btn">
                  Apply all
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setSuggestions(null)} className="rounded-full text-stone-500" data-testid="dismiss-suggestions-btn">
                  Dismiss
                </Button>
              </div>
            </div>
            <div className="mt-4 grid gap-2 md:grid-cols-2">
              {Object.entries(suggestions.suggestions || {}).map(([subj, s]) => (
                <div key={subj} className="rounded-xl bg-white border border-stone-200 px-3 py-2 text-sm flex items-start gap-2" data-testid={`suggestion-${subj}`}>
                  <div className="min-w-0 flex-1">
                    <span className="font-semibold text-stone-900">{subj}</span>
                    <span className="mx-2 text-stone-400">→</span>
                    <span className="inline-flex items-center rounded-full bg-indigo-100 text-indigo-700 text-xs font-semibold px-2 py-0.5">{s.style}</span>
                    {s.reason && <div className="mt-1 text-xs text-stone-500 leading-snug">{s.reason}</div>}
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-3 text-[10px] text-stone-400 uppercase tracking-[0.15em]">Suggestions are not saved until you click Apply all + Save profile.</p>
          </div>
        )}

        <div className="mt-6 space-y-6">
          {profile.subjects_catalog.map((subject) => {
            const entry = scores[subject] || { score: 60, notes: "" };
            const band = scoreBand(entry.score);
            const focus = (profile.grade_focus || {})[subject];
            return (
              <div key={subject} className="rounded-2xl border border-stone-200 p-5" data-testid={`prof-${subject}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-display text-lg font-semibold">{subject}</div>
                      <span className={`text-xs font-semibold uppercase tracking-[0.15em] ${band.color}`}>{band.label}</span>
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-stone-400" data-testid={`assessed-${subject}`}>
                        <Clock className="w-3 h-3" /> Assessed {relativeTime(entry.last_assessed)}
                      </span>
                    </div>
                    {focus && focus.length > 0 && (
                      <div className="mt-1 text-xs text-stone-500">Grade {profile.grade} focus: {focus.join(" · ")}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <div data-testid={`sparkline-${subject}`}>
                      <Sparkline points={history[subject] || []} />
                    </div>
                    <div className="font-display text-2xl font-bold w-14 text-right">{entry.score}</div>
                  </div>
                </div>
                <div className="mt-4">
                  <Slider
                    value={[entry.score]}
                    min={0}
                    max={100}
                    step={5}
                    onValueChange={(v) => setScores((prev) => ({ ...prev, [subject]: { ...entry, score: v[0] } }))}
                    data-testid={`slider-${subject}`}
                  />
                </div>
                <div className="mt-3 grid gap-3 md:grid-cols-[1fr,220px]">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-stone-500">Notes</Label>
                    <Textarea
                      value={entry.notes}
                      onChange={(e) => setScores((prev) => ({ ...prev, [subject]: { ...entry, notes: e.target.value } }))}
                      placeholder="What specific skills are strong or need work?"
                      className="mt-2 rounded-xl min-h-[60px]"
                      data-testid={`notes-${subject}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-stone-500">Learning style for this subject</Label>
                    <select
                      value={entry.learning_style || ""}
                      onChange={(e) => setScores((prev) => ({ ...prev, [subject]: { ...entry, learning_style: e.target.value } }))}
                      className="mt-2 w-full h-10 rounded-xl border border-stone-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                      data-testid={`style-${subject}`}
                    >
                      <option value="">Use default ({profile.learning_style})</option>
                      <option value="visual">Visual</option>
                      <option value="auditory">Auditory</option>
                      <option value="kinesthetic">Kinesthetic</option>
                      <option value="reading">Reading/Writing</option>
                    </select>
                    <div className="mt-1.5 text-[10px] text-stone-400">
                      Worksheets, help & schedule use this per-subject style.
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Assessments panel — upload WRAT-5 + manual entry */}
      <AssessmentsPanel studentId={id} onAppliedUpdates={() => load()} />

      {/* Portfolio artifacts — external documents (worksheets, art, book reports) */}
      <PortfolioDocumentsPanel studentId={id} subjects={profile.subjects_catalog || []} />

      {/* Bible-verse memory tracker (Christian curriculum only) */}
      {profile.christian_curriculum && <VerseTracker studentId={id} />}

      {/* Attendance & instructional hours (FL 1002.41) */}
      <AttendanceCard studentId={id} />

      {/* Recent lessons */}
      <div className="bg-white border border-stone-200 rounded-3xl p-8 shadow-sm">
        <h2 className="font-display text-2xl font-semibold">Recent lessons</h2>
        {profile.recent_lessons.length === 0 ? (
          <p className="mt-2 text-sm text-stone-500">No lessons yet. Use auto-build to fill a week in one click.</p>
        ) : (
          <div className="mt-4 divide-y divide-stone-100">
            {profile.recent_lessons.slice(0, 10).map((l) => (
              <div key={l.id} className="py-3 flex items-center gap-4">
                <div className="w-14 text-center">
                  {(() => { const [y, m, dd] = l.date.split("-").map(n => parseInt(n, 10)); const dt = new Date(y, m - 1, dd); return (<>
                    <div className="text-xs uppercase tracking-[0.15em] text-stone-500">{dt.toLocaleString("en-US", { month: "short" })}</div>
                    <div className="font-display text-xl font-bold">{dt.getDate()}</div>
                  </>); })()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{l.title}</div>
                  <div className="text-xs text-stone-500 truncate">{l.subject} · {l.time || "—"} · {l.status}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
